import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useRegister } from "@/features/auth/api/useAuth";
import {
  PASSWORD_MIN_LENGTH,
  registerSchema,
  type RegisterValues,
} from "@/features/auth/lib/schemas";
import { useApiErrorMessage } from "@/features/auth/lib/useApiErrorMessage";
import { ApiError } from "@/shared/api/errors";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { Field } from "@/shared/ui/Field";

export function RegisterPage() {
  const { t, i18n } = useTranslation("auth");
  const registration = useRegister();
  const errorMessage = useApiErrorMessage();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { role: "student" },
  });

  const onSubmit = handleSubmit((values) => {
    // La locale courante est transmise explicitement : c'est elle qui décide de
    // la langue de l'email de vérification, que le serveur enverra depuis un
    // worker sans requête HTTP dont s'inspirer (docs/22 §5).
    registration.mutate({ ...values, locale: i18n.language });
  });

  if (registration.isSuccess) {
    return (
      <Card className="mx-auto max-w-md">
        <h1 className="mb-4 text-2xl font-bold tracking-tight">{t("register.doneHeading")}</h1>
        <Alert tone="success">{t("register.doneBody")}</Alert>
        <p className="mt-6 text-sm">
          <Link to="/login" className="text-brand-600 hover:underline">
            {t("register.goToLogin")}
          </Link>
        </p>
      </Card>
    );
  }

  return (
    <Card className="mx-auto max-w-md">
      <h1 className="mb-6 text-2xl font-bold tracking-tight">{t("register.heading")}</h1>

      {registration.isError && (
        <Alert tone="error" className="mb-4">
          {errorMessage(registration.error)}
          {/* Le serveur détaille POURQUOI le mot de passe est refusé : trop
              court, trop courant, contient le nom. Ces raisons ont chacune leur
              code stable, donc leur traduction (docs/06 §6). */}
          <PasswordViolations error={registration.error} />
        </Alert>
      )}

      <form
        onSubmit={(event) => {
          // `handleSubmit` rend toujours une fonction asynchrone, même sur un
          // gestionnaire synchrone : `void` dit explicitement qu'on n'attend
          // pas la promesse, ce que React ne saurait pas faire de toute façon.
          void onSubmit(event);
        }}
        className="space-y-4"
        noValidate
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t("fields.firstName")}
            autoComplete="given-name"
            error={errors.first_name && t("validation.required")}
            {...register("first_name")}
          />
          <Field
            label={t("fields.lastName")}
            autoComplete="family-name"
            error={errors.last_name && t("validation.required")}
            {...register("last_name")}
          />
        </div>
        <Field
          label={t("fields.email")}
          type="email"
          autoComplete="email"
          error={errors.email && t("validation.email")}
          {...register("email")}
        />
        <Field
          label={t("fields.password")}
          type="password"
          autoComplete="new-password"
          hint={t("validation.passwordHint", { count: PASSWORD_MIN_LENGTH })}
          error={
            errors.password && t("validation.passwordTooShort", { count: PASSWORD_MIN_LENGTH })
          }
          {...register("password")}
        />

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">{t("fields.role")}</legend>
          {(["student", "teacher"] as const).map((role) => (
            <label key={role} className="flex items-center gap-2 text-sm">
              <input type="radio" value={role} {...register("role")} />
              {t(`roles.${role}`)}
            </label>
          ))}
        </fieldset>

        <Button type="submit" className="w-full" disabled={registration.isPending}>
          {registration.isPending ? t("register.submitting") : t("register.submit")}
        </Button>
      </form>

      <p className="mt-6 text-sm text-slate-600 dark:text-slate-400">
        {t("register.haveAccount")}{" "}
        <Link to="/login" className="text-brand-600 hover:underline">
          {t("register.loginLink")}
        </Link>
      </p>
    </Card>
  );
}

function PasswordViolations({ error }: { error: unknown }) {
  const { t } = useTranslation("auth");
  if (!(error instanceof ApiError) || error.fieldErrors.length === 0) {
    return null;
  }
  return (
    <ul className="mt-2 list-inside list-disc">
      {error.fieldErrors.map((violation) => (
        <li key={`${violation.field}.${violation.code}`}>
          {t([`violations.${violation.code}`, "violations.unknown"])}
        </li>
      ))}
    </ul>
  );
}
